export type SatusehatStatus = "terkirim" | "gagal" | "tidak_dikirim" | "menunggu";

export type SatusehatAlasan =
  | "unit_belum_dipetakan"
  | "lokasi_tanpa_ihs"
  | "pasien_tanpa_ihs"
  | "dokter_tanpa_ihs"
  | "tanpa_diagnosis"
  | "tidak_diproses"
  | "alasan_tidak_terperiksa";

export type SatusehatPemeriksaanKey = "lokasi" | "pasien" | "dokter" | "diagnosis";

export type SatusehatJenisKirimUlang = "kunjungan" | "obat";

export type SatusehatSebabKirimUlang =
  | "sudah_terkirim"
  | "menunggu_jadwal"
  | "data_belum_lengkap"
  | "kunjungan_belum_terkirim"
  | "tidak_ada_obat_gagal";

export type SatusehatHasilKirimUlang = "terkirim" | "gagal" | "dilewati" | "galat_koneksi";

export interface SatusehatKelayakanKirimUlang {
  boleh: boolean;
  sebab: SatusehatSebabKirimUlang | null;
}

export interface SatusehatKirimUlangResponse {
  id: number;
  register: string;
  jenis: SatusehatJenisKirimUlang;
  hasil: SatusehatHasilKirimUlang;
  pesan: string;
}

export interface SatusehatKodeNama {
  kode: string | null;
  nama: string | null;
}

export interface SatusehatUnit {
  kode: string;
  nama: string;
}

export interface SatusehatAktivitas {
  bundle_terakhir: string | null;
  obat_terakhir: string | null;
  basi: boolean | null;
  ambang_jam: number;
}

export interface SatusehatTrenRow {
  tgl: string;
  kunjungan: number | null;
  terkirim: number | null;
  gagal: number | null;
  belum: number | null;
  persen: number | null;
}

export interface SatusehatRingkasanResponse {
  aktivitas: SatusehatAktivitas;
  tren: SatusehatTrenRow[];
  units: SatusehatUnit[];
}

export interface SatusehatObatHitung {
  terkirim: number;
  gagal: number;
}

export interface SatusehatKunjunganRow {
  register: string;
  rm: string;
  nama_pasien: string | null;
  unit: SatusehatKodeNama;
  dokter: SatusehatKodeNama;
  status: SatusehatStatus;
  alasan: SatusehatAlasan[];
  encounter_id: string | null;
  salinan: number;
  percobaan: number;
  waktu: string | null;
  galat: string | null;
  obat: SatusehatObatHitung;
  hambatan: SatusehatAlasan[];
  kirim_ulang: Record<SatusehatJenisKirimUlang, SatusehatKelayakanKirimUlang>;
}

export interface SatusehatObatRow {
  id: number;
  register: string | null;
  rm: string | null;
  nama_pasien: string | null;
  local_id: string | null;
  jenis: string;
  terkirim: boolean;
  ihs_id: string | null;
  galat: string | null;
  waktu: string | null;
}

export interface SatusehatAlasanHitung {
  kode: SatusehatAlasan;
  jumlah: number;
}

export type SatusehatRingkasanStatus = Record<SatusehatStatus, number> & { total: number };

export interface SatusehatKunjunganResponse {
  tgl: string;
  menunggu_jadwal: boolean;
  ringkasan: SatusehatRingkasanStatus;
  alasan: SatusehatAlasanHitung[];
  pemeriksaan: Record<SatusehatPemeriksaanKey, boolean>;
  rows: SatusehatKunjunganRow[];
  obat: SatusehatObatRow[];
  sumber_gagal: string[];
}

export interface SatusehatStatusTampilan {
  label: string;
  color: string;
  icon: string;
}

export const SATUSEHAT_STATUS: Record<SatusehatStatus, SatusehatStatusTampilan> = {
  terkirim: { label: "Terkirim", color: "green", icon: "mdi-cloud-check-outline" },
  gagal: { label: "Gagal", color: "red", icon: "mdi-cloud-alert" },
  tidak_dikirim: { label: "Tidak dikirim", color: "orange darken-2", icon: "mdi-cloud-off-outline" },
  menunggu: { label: "Menunggu jadwal", color: "blue-grey", icon: "mdi-clock-outline" }
};

export const SATUSEHAT_ALASAN: Record<SatusehatAlasan, string> = {
  unit_belum_dipetakan: "Unit belum dipetakan ke Location",
  lokasi_tanpa_ihs: "Location belum terdaftar di SATUSEHAT",
  pasien_tanpa_ihs: "Pasien belum punya IHS Number",
  dokter_tanpa_ihs: "DPJP belum punya IHS Number",
  tanpa_diagnosis: "Belum ada diagnosis dokter",
  tidak_diproses: "Tidak diproses api-satusehat",
  alasan_tidak_terperiksa: "Alasan belum dapat dipastikan"
};

export const SATUSEHAT_JENIS_KIRIM_ULANG: Record<SatusehatJenisKirimUlang, string> = {
  kunjungan: "kunjungan (bundle Encounter, diagnosis, dll.)",
  obat: "obat yang gagal"
};

export const SATUSEHAT_SEBAB_KIRIM_ULANG: Record<SatusehatSebabKirimUlang, string> = {
  sudah_terkirim: "Kunjungan sudah terkirim.",
  menunggu_jadwal: "Belum jadwal kirim — api-satusehat memprosesnya H+1.",
  data_belum_lengkap: "Perbaiki data dulu:",
  kunjungan_belum_terkirim: "Obat baru dapat dikirim ulang setelah Encounter kunjungannya tercatat terkirim.",
  tidak_ada_obat_gagal: "Tidak ada obat yang gagal."
};

export const SATUSEHAT_HASIL_KIRIM_ULANG: Record<SatusehatHasilKirimUlang, "success" | "error" | "warning"> = {
  terkirim: "success",
  gagal: "error",
  dilewati: "warning",
  galat_koneksi: "error"
};

export const SATUSEHAT_PEMERIKSAAN: Record<SatusehatPemeriksaanKey, string> = {
  lokasi: "pemetaan unit ke Location",
  pasien: "IHS Number pasien",
  dokter: "IHS Number DPJP",
  diagnosis: "diagnosis dokter"
};
